import {
  getJavaScriptTipsAndNotesMarkdown,
  javascriptAccessPreviousDataMarkdown,
  javascriptLoggingAndDebuggingMarkdown,
  javascriptNotableSdkMarkdown,
} from '../../ConfigFields/JavaScriptBlock/documentation';

export const SCRIPTED_FLOW_DOCUMENTATION_MARKDOWN = `
When to Use
-----------

*   When existing blocks don't provide the exact functionality you need
*   For custom calculations or data transformations
*   To combine or filter data from multiple upstream blocks

How to Use
----------

### Basic Structure

Your code should be written as if it's the body of a function. Use the return statement to output data that will be available to downstream blocks.

~~~javascript
// Process data here
const aNewVariable = 'someValue';
const processedData = aNewVariable.toUpperCase();
return processedData;  // This will be available to next blocks
~~~

${javascriptAccessPreviousDataMarkdown}
${javascriptLoggingAndDebuggingMarkdown}
${javascriptNotableSdkMarkdown}

Examples
--------

### 1. Basic Calculation
~~~javascript
/**
 * Calculate BMI from height and weight
 */
const height = patientMetrics.data.heightInMeters;
const weight = patientMetrics.data.weightInKg;
const bmi = weight / (height * height);
return {
  bmi: bmi.toFixed(1),
  category: bmi < 18.5 ? "Underweight" :
            bmi < 25 ? "Normal" :
            bmi < 30 ? "Overweight" : "Obese"
};
~~~

### 2. Data Filtering

~~~javascript
/**
 * Filter lab results to only show abnormal values
 */
const labResults = labData.data.results;
const abnormalResults = labResults.filter(result =>
  result.value < result.normalRange.min ||
  result.value > result.normalRange.max
);
return {
  abnormalCount: abnormalResults.length,
  results: abnormalResults
};
~~~

### 3. Combining Data

~~~javascript
/**
 * Combine patient demographics with appointment info
 */
const demographics = patientInfo.data;
const appointment = scheduleInfo.data;
return {
  patientName: demographics.name,
  age: demographics.age,
  appointmentDate: appointment.date,
  provider: appointment.provider,
  location: appointment.location
};
~~~

### 4. Parse CSV into an object
~~~javascript
const csvString = \`id,first_name,last_name,email,status,last_login
1,Abraham,Lincoln,abe@example.com,active,2024-02-20
2,Frederick,Douglass,fred@example.com,trial,2024-02-19\`;
return ntbl.parseCsv(csvString, {
  /* If true, the first row of parsed data will be interpreted as field names. */
  header: true,
  /* if true, numeric and boolean data will be converted to their type instead of remaining strings.  */
  dynamicTyping: true
});
~~~

Output looks like:
~~~json
{
  "data": [
    {
      "id": 1,
      "email": "abe@email.com",
      "status": "active",
      "last_name": "Lincoln",
      "first_name": "Abraham",
      "last_login": "2024-02-20"
    },
    {
      "id": 2,
      "email": "fred@example.com",
      "status": "trial",
      "last_name": "Douglass",
      "first_name": "Frederick",
      "last_login": "2024-02-19"
    }
  ],
  "meta": {
    "cursor": 148,
    "fields": [
      "id",
      "first_name",
      "last_name",
      "email",
      "status",
      "last_login"
    ],
    "aborted": false,
    "delimiter": ",",
    "linebreak": "\\n",
    "truncated": false,
    "renamedHeaders": null
  },
  "errors": []
}
~~~

${getJavaScriptTipsAndNotesMarkdown({ errorHandlingVariant: 'ReturnObject' })}
`;
